import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, Globe, Plus, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Banner } from '../../components/Banner';
import { PageHeader } from '../../components/PageHeader';
import { useResellerDomains, addResellerDomain, removeResellerDomain, verifyResellerDomain } from '../../api/reseller';

/** Reseller domains list with add-domain modal and DNS verification status. */
export function Domains() {
  const qc = useQueryClient();
  const { data: rows = [] } = useResellerDomains();
  const [addOpen, setAddOpen] = useState(false);
  const [newHost, setNewHost] = useState('');
  const [busy, setBusy] = useState(false);
  const [recheckingId, setRecheckingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function addDomain() {
    const host = newHost.trim().toLowerCase();
    if (!host || busy) return;
    setBusy(true);
    setError(null);
    try {
      await addResellerDomain(host);
      await qc.invalidateQueries({ queryKey: ['reseller', 'domains'] });
      setNewHost('');
      setAddOpen(false);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(
        status === 409
          ? 'That domain is already registered.'
          : status === 400
            ? 'Enter a valid domain, e.g. pos.example.com.'
            : 'Could not add the domain.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function removeRow(id: string) {
    if (busy) return;
    setBusy(true);
    try {
      await removeResellerDomain(id);
      await qc.invalidateQueries({ queryKey: ['reseller', 'domains'] });
    } finally {
      setBusy(false);
    }
  }

  async function recheckRow(id: string) {
    if (recheckingId) return;
    setRecheckingId(id);
    try {
      await verifyResellerDomain(id);
      await qc.invalidateQueries({ queryKey: ['reseller', 'domains'] });
    } finally {
      setRecheckingId(null);
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1200px] mx-auto">
      <PageHeader
        eyebrow="Reseller"
        title="Domains"
        subtitle="Subdomains and custom domains pointed at your reseller account."
        action={
          <Button iconLeft={<Plus className="w-4 h-4" />} onClick={() => setAddOpen(true)}>
            Add domain
          </Button>
        }
      />

      <Banner tone="info" className="mb-4">
        After adding a domain, create a CNAME record pointing it to{' '}
        <code className="mono font-medium">cname.itcitysoft.com</code>. We check DNS automatically every few minutes —
        use <span className="font-medium">Recheck now</span> on a row if you don't want to wait. Once DNS is verified,
        our team activates the TLS certificate for it.
      </Banner>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-700">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Hostname</th>
                <th className="text-left font-medium px-4 py-3">Pointed at</th>
                <th className="text-left font-medium px-4 py-3">DNS</th>
                <th className="text-left font-medium px-4 py-3">TLS</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
                      <span className="mono text-xs sm:text-sm text-zinc-700 dark:text-zinc-200">{r.hostname}</span>
                      {r.isPrimary && <Badge tone="info">Primary</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-zinc-600 dark:text-zinc-300">
                    <span className="text-xs">Reseller portal</span>
                  </td>
                  <td className="px-4 py-4">
                    {r.dnsStatus === 'verified' ? (
                      <Badge tone="success">Verified</Badge>
                    ) : r.dnsStatus === 'failed' ? (
                      <Badge tone="error">Failed</Badge>
                    ) : (
                      <Badge tone="warning">Pending</Badge>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {r.tlsStatus === 'issued' ? (
                      <Badge tone="success">
                        <ShieldCheck className="w-3 h-3" />
                        Issued
                      </Badge>
                    ) : r.tlsStatus === 'pending' ? (
                      <Badge tone="warning">Pending</Badge>
                    ) : (
                      <Badge tone="error">Failed</Badge>
                    )}
                  </td>
                  <td className="px-5 sm:px-6 py-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText('cname.itcitysoft.com')}
                        className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                        title="Copy CNAME target"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      {r.dnsStatus !== 'verified' && (
                        <button
                          type="button"
                          onClick={() => recheckRow(r.id)}
                          disabled={recheckingId === r.id}
                          className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition disabled:opacity-50"
                          title="Recheck now"
                        >
                          <RefreshCw className={`w-4 h-4 ${recheckingId === r.id ? 'animate-spin' : ''}`} />
                        </button>
                      )}
                      {!r.isPrimary && (
                        <button
                          type="button"
                          onClick={() => removeRow(r.id)}
                          className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40 dark:hover:text-rose-400 transition"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add domain modal */}
      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add a domain"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={addDomain} loading={busy} disabled={!newHost || busy}>
              Add domain
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <Input
            label="Hostname"
            required
            placeholder="pos.your-domain.com"
            value={newHost}
            onChange={(e) => setNewHost(e.target.value.toLowerCase())}
            hint="Don't include http:// or trailing slashes"
          />
          {newHost && (
            <div className="rounded-md p-4 bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 text-sm">
              <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
                Add this CNAME record at your DNS provider
              </div>
              <div className="font-mono text-xs space-y-1 text-zinc-700 dark:text-zinc-200">
                <div>
                  <span className="text-zinc-500 dark:text-zinc-400">Type:</span> CNAME
                </div>
                <div>
                  <span className="text-zinc-500 dark:text-zinc-400">Name:</span>{' '}
                  {newHost.split('.')[0] ?? newHost}
                </div>
                <div>
                  <span className="text-zinc-500 dark:text-zinc-400">Value:</span> cname.itcitysoft.com
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
